import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Tooltip,
  MenuItem,
} from '@mui/material';
import { Add, Delete, Edit, Refresh } from '@mui/icons-material';
import {
  getTenantPagingSettingsAdmin,
  upsertTenantPagingSettingsAdmin,
  getTenantPagingRolePoliciesAdmin,
  setTenantPagingRolePoliciesAdmin,
  getTenantPagingZonesAdmin,
  createTenantPagingZoneAdmin,
  updateTenantPagingZoneAdmin,
  deleteTenantPagingZoneAdmin,
  getTenantPagingZoneDevicesAdmin,
  setTenantPagingZoneDevicesAdmin,
  searchTenantUsersAdmin,
  fetchUsersFromAuth,
  getTenantSegmentsAdmin,
} from '../../../api/backendApi';
import TroubleshootThis from '../../../assistant/TroubleshootThis';
import { segmentPickerChoices, segmentPickerValue } from '../../../utils/segmentDedup';

function ZoneDialog({ open, onClose, onSubmit, initial, devices, loadingDevices, tenantId, userLabelMap: globalUserLabelMap }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);
  const [defaultDuration, setDefaultDuration] = useState('');
  const [maxDuration, setMaxDuration] = useState('');
  const [allowedRolesText, setAllowedRolesText] = useState('');
  const [allowedUserIds, setAllowedUserIds] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [localUserLabelMap, setLocalUserLabelMap] = useState({});
  const [multicastRows, setMulticastRows] = useState([{ group: '', port: '', ttl: '', gainDb: '' }]);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState(new Set());
  const [segmentId, setSegmentId] = useState('');
  const [segments, setSegments] = useState([]);
  const [segmentsLoading, setSegmentsLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogTab, setDialogTab] = useState('paging');
  const SHOW_ZONE_DEVICES = false;

  useEffect(() => {
    const z = initial || {};
    setName(z.name || '');
    setDescription(z.description || '');
    setActive(z.active !== false);
    setDefaultDuration(
      typeof z.defaultDurationSeconds === 'number' && !Number.isNaN(z.defaultDurationSeconds)
        ? String(z.defaultDurationSeconds)
        : ''
    );
    setMaxDuration(
      typeof z.maxDurationSeconds === 'number' && !Number.isNaN(z.maxDurationSeconds)
        ? String(z.maxDurationSeconds)
        : ''
    );
    const rolesArray = Array.isArray(z.allowedRoles) ? z.allowedRoles : [];
    const isNewZone = !z.id;
    if (rolesArray.length > 0) {
      setAllowedRolesText(rolesArray.join(', '));
    } else if (isNewZone) {
      setAllowedRolesText('admin, tenant_admin');
    } else {
      setAllowedRolesText('');
    }
    setAllowedUserIds(Array.isArray(z.allowedUserIds) ? z.allowedUserIds : []);
    setUserSearchTerm('');
    setUserSearchResults([]);
    setLocalUserLabelMap({});
    setSegmentId(z.segmentId || '');
    const mg = Array.isArray(z.multicastGroups) && z.multicastGroups.length > 0
      ? z.multicastGroups.map((m) => ({
          group: m.group || '',
          port: m.port != null ? String(m.port) : '',
          ttl: m.ttl != null ? String(m.ttl) : '',
          gainDb: m.gainDb != null ? String(m.gainDb) : '',
        }))
      : [{ group: '', port: '', ttl: '', gainDb: '' }];
    setMulticastRows(mg);

    if (Array.isArray(devices)) {
      const initialSelected = new Set();
      devices.forEach((d) => {
        if (d.inZone) initialSelected.add(d.id);
      });
      setSelectedDeviceIds(initialSelected);
    } else {
      setSelectedDeviceIds(new Set());
    }
    setError('');
    setDialogTab('paging');
  }, [initial, devices, open]);

  useEffect(() => {
    if (!open || !tenantId) return;
    const loadSegments = async () => {
      setSegmentsLoading(true);
      try {
        const segs = await getTenantSegmentsAdmin(tenantId);
        setSegments(Array.isArray(segs) ? segs : []);
      } catch (e) {
        console.error('Failed to load segments:', e);
        setSegments([]);
      } finally {
        setSegmentsLoading(false);
      }
    };
    loadSegments();
  }, [open, tenantId]);

  useEffect(() => {
    let active = true;
    const runSearch = async () => {
      const term = userSearchTerm.trim();
      if (!open || !tenantId || term.length < 2) {
        setUserSearchResults([]);
        return;
      }
      setUserSearchLoading(true);
      try {
        const results = await searchTenantUsersAdmin(tenantId, term, 25);
        if (!active) return;
        const list = Array.isArray(results) ? results : [];
        setUserSearchResults(list);

        // Cache friendly labels for any users returned by search
        setLocalUserLabelMap((prev) => {
          const next = { ...prev };
          list.forEach((u) => {
            const uid = u.id;
            if (!uid) return;
            const label =
              u.displayName ||
              [u.firstName, u.lastName].filter(Boolean).join(' ') ||
              u.email ||
              uid;
            const email = u.email || '';
            next[uid] = { label, email };
          });
          return next;
        });
      } catch (e) {
        if (!active) return;
        setUserSearchResults([]);
      } finally {
        if (active) setUserSearchLoading(false);
      }
    };
    const id = setTimeout(runSearch, 300);
    return () => {
      active = false;
      clearTimeout(id);
    };
  }, [userSearchTerm, tenantId, open]);

  // When opening an existing zone, resolve display labels for any pre-assigned user IDs
  useEffect(() => {
    if (!open) return;
    if (!Array.isArray(allowedUserIds) || allowedUserIds.length === 0) return;

    setLocalUserLabelMap((prev) => {
      const next = { ...prev };
      allowedUserIds.forEach((uid) => {
        if (!uid) return;
        const globalInfo = globalUserLabelMap && globalUserLabelMap[uid];
        if (globalInfo && !next[uid]) {
          next[uid] = globalInfo;
        }
      });
      return next;
    });
  }, [open, allowedUserIds, globalUserLabelMap]);

  const handleMulticastChange = (index, field, value) => {
    setMulticastRows((rows) => {
      const next = rows.map((r, i) => (i === index ? { ...r, [field]: value } : r));
      return next;
    });
  };

  const handleAddMulticastRow = () => {
    setMulticastRows((rows) => [...rows, { group: '', port: '', ttl: '', gainDb: '' }]);
  };

  const handleRemoveMulticastRow = (index) => {
    setMulticastRows((rows) => {
      if (rows.length === 1) return rows;
      return rows.filter((_, i) => i !== index);
    });
  };

  const toggleDevice = (id) => {
    setSelectedDeviceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setDialogTab('paging');
      setError('Name is required');
      return;
    }

    const parsedMulticast = multicastRows
      .map((r) => {
        const group = (r.group || '').trim();
        const portNum = r.port != null && String(r.port).trim() !== '' ? Number(r.port) : null;
        const ttlNum = r.ttl != null && String(r.ttl).trim() !== '' ? Number(r.ttl) : null;
        const gainNum = r.gainDb != null && String(r.gainDb).trim() !== '' ? Number(r.gainDb) : null;
        if (!group || !portNum || Number.isNaN(portNum)) {
          return null;
        }
        const out = { group, port: portNum };
        if (ttlNum && !Number.isNaN(ttlNum)) out.ttl = ttlNum;
        if (gainNum && !Number.isNaN(gainNum)) out.gainDb = gainNum;
        return out;
      })
      .filter(Boolean);

    const allowedRoles = allowedRolesText
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const payload = {
      name: name.trim(),
      description: description || '',
      active: !!active,
      multicastGroups: parsedMulticast,
      allowedRoles,
      allowedUserIds,
    };

    if (segmentId && segmentId.trim()) {
      payload.segmentId = segmentId.trim();
    }

    const defDur = Number(defaultDuration);
    if (!Number.isNaN(defDur) && defDur > 0) payload.defaultDurationSeconds = defDur;
    const maxDur = Number(maxDuration);
    if (!Number.isNaN(maxDur) && maxDur > 0) payload.maxDurationSeconds = maxDur;

    const deviceIds = Array.from(selectedDeviceIds);
    await onSubmit(payload, deviceIds);
  };

  const isEdit = !!initial?.id;
  const segmentChoices = useMemo(() => segmentPickerChoices(segments), [segments]);
  const selectedSegment = segmentPickerValue(segmentId, segmentChoices);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 0 }}>
        {isEdit ? 'Edit Paging Zone' : 'Add Paging Zone'}
        <Tabs
          value={dialogTab}
          onChange={(_event, value) => setDialogTab(value)}
          sx={{ mt: 1 }}
        >
          <Tab label="RTP Paging" value="paging" />
          <Tab label="Assignment" value="assignment" />
        </Tabs>
      </DialogTitle>
      <DialogContent dividers>
        {dialogTab === 'paging' && (
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Zone Name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!error}
            helperText={error || ''}
          />

          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <Box>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <TextField
                select
                label="Subnet"
                fullWidth
                value={selectedSegment}
                onChange={(e) => setSegmentId(e.target.value)}
                disabled={segmentsLoading}
                helperText="The network the speakers are on. Set this when a page starts from the browser, so the server can use an agent at that site. Leave it empty when paging starts only on the agent."
              >
                <MenuItem value="">No subnet</MenuItem>
                {segmentChoices.map((choice) => (
                  <MenuItem key={choice.value} value={choice.value}>
                    {choice.label}
                  </MenuItem>
                ))}
              </TextField>
              {segmentsLoading && <CircularProgress size={24} sx={{ alignSelf: 'center', mt: 1 }} />}
            </Stack>
          </Box>

          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel
              control={<Switch checked={!!active} onChange={(e) => setActive(e.target.checked)} />}
              label="Active"
            />
            <TextField
              label="Default Duration (seconds)"
              type="number"
              value={defaultDuration}
              onChange={(e) => setDefaultDuration(e.target.value)}
              sx={{ width: 220 }}
            />
            <TextField
              label="Max Duration (seconds)"
              type="number"
              value={maxDuration}
              onChange={(e) => setMaxDuration(e.target.value)}
              sx={{ width: 220 }}
            />
          </Stack>

          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle1">Multicast Groups</Typography>
              <Button startIcon={<Add />} onClick={handleAddMulticastRow} size="small" variant="outlined">
                Add Group
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Gain is a volume multiplier for offline paging on the agent. 1, 0, or blank leaves the audio unchanged. 2 plays it twice as loud. Live pages ignore this. It is not decibels.
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Group</TableCell>
                  <TableCell>Port</TableCell>
                  <TableCell>TTL</TableCell>
                  <TableCell>Gain</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {multicastRows.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <TextField
                        size="small"
                        value={row.group}
                        onChange={(e) => handleMulticastChange(idx, 'group', e.target.value)}
                        placeholder="239.0.0.1"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={row.port}
                        onChange={(e) => handleMulticastChange(idx, 'port', e.target.value)}
                        placeholder="5000"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={row.ttl}
                        onChange={(e) => handleMulticastChange(idx, 'ttl', e.target.value)}
                        placeholder="1"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={row.gainDb}
                        onChange={(e) => handleMulticastChange(idx, 'gainDb', e.target.value)}
                        placeholder="0"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleRemoveMulticastRow(idx)} disabled={multicastRows.length === 1}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {isEdit && SHOW_ZONE_DEVICES && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle1">Devices in Zone</Typography>
                {loadingDevices && <CircularProgress size={20} />}
              </Stack>
              {Array.isArray(devices) && devices.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>In Zone</TableCell>
                      <TableCell>Device ID</TableCell>
                      <TableCell>Info</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {devices.map((d) => (
                      <TableRow key={d.id} hover>
                        <TableCell>
                          <Checkbox
                            size="small"
                            checked={selectedDeviceIds.has(d.id)}
                            onChange={() => toggleDevice(d.id)}
                          />
                        </TableCell>
                        <TableCell>{d.id}</TableCell>
                        <TableCell>
                          {d.name || d.label || d.model || d.ip || '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {loadingDevices ? 'Loading devices...' : 'No devices available for this tenant.'}
                </Typography>
              )}
            </Box>
          )}
        </Stack>
        )}
        {dialogTab === 'assignment' && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Allowed Users
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                People in this list can start RTP pages for this zone.
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                <TextField
                  label="Search users by name or email"
                  fullWidth
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  helperText="Type at least 2 characters to search"
                />
                {userSearchLoading && <CircularProgress size={24} sx={{ alignSelf: 'center' }} />}
              </Stack>
              {userSearchResults.length > 0 && (
                <Box
                  sx={{
                    maxHeight: 240,
                    overflowY: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    p: 1,
                  }}
                >
                  {userSearchResults.map((u) => {
                    const uid = u.id;
                    const already = allowedUserIds.includes(uid);
                    const label =
                      u.displayName ||
                      [u.firstName, u.lastName].filter(Boolean).join(' ') ||
                      u.email ||
                      uid;
                    return (
                      <Stack
                        key={uid}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ py: 0.5 }}
                      >
                        <Checkbox
                          size="small"
                          checked={already}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setAllowedUserIds((prev) => {
                              if (checked) {
                                if (prev.includes(uid)) return prev;
                                return [...prev, uid];
                              }
                              return prev.filter((id) => id !== uid);
                            });
                          }}
                        />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {label}
                          {u.email && label !== u.email && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              {u.email}
                            </Typography>
                          )}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Box>
              )}
              {allowedUserIds.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {allowedUserIds.map((uid) => {
                    const info = localUserLabelMap[uid] || (globalUserLabelMap && globalUserLabelMap[uid]);
                    const label = info?.label || uid;
                    return (
                      <Chip
                        key={uid}
                        label={label}
                        size="small"
                        onDelete={() =>
                          setAllowedUserIds((prev) => prev.filter((id) => id !== uid))
                        }
                      />
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No users assigned yet.
                </Typography>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          {isEdit ? 'Save Changes' : 'Create Zone'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PagingTab({ tenantId }) {
  const [settings, setSettings] = useState({ pagingEnabled: true });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState('');

  const [rolePolicies, setRolePolicies] = useState({});
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesSaving, setRolesSaving] = useState(false);
  const [rolesError, setRolesError] = useState('');

  const [zones, setZones] = useState([]);
  const [zonesLoading, setZonesLoading] = useState(false);
  const [zonesError, setZonesError] = useState('');

  const [zoneDialogOpen, setZoneDialogOpen] = useState(false);
  const [zoneDialogInitial, setZoneDialogInitial] = useState(null);
  const [zoneDialogSaving, setZoneDialogSaving] = useState(false);
  const [zoneDevices, setZoneDevices] = useState([]);
  const [zoneDevicesLoading, setZoneDevicesLoading] = useState(false);

  const [userLabelMap, setUserLabelMap] = useState({});

  const SHOW_PAGING_ROLE_POLICIES = false;

  useEffect(() => {
    if (!tenantId) return;
    loadAll();
  }, [tenantId]);

  const loadAll = async () => {
    await Promise.all([loadSettings(), loadZones(), loadRolePolicies()]);
  };

  const loadSettings = async () => {
    if (!tenantId) return;
    setSettingsLoading(true);
    setSettingsError('');
    try {
      const data = await getTenantPagingSettingsAdmin(tenantId);
      setSettings({
        pagingEnabled: data.pagingEnabled !== false,
        globalDefaultDurationSeconds: data.globalDefaultDurationSeconds || '',
        globalMaxDurationSeconds: data.globalMaxDurationSeconds || '',
        defaultTtl: data.defaultTtl || '',
      });
    } catch (e) {
      setSettingsError(String(e?.message || 'Failed to load paging settings'));
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!tenantId) return;
    setSettingsSaving(true);
    setSettingsError('');
    try {
      const payload = {
        pagingEnabled: !!settings.pagingEnabled,
      };
      const defDur = Number(settings.globalDefaultDurationSeconds);
      if (!Number.isNaN(defDur) && defDur > 0) payload.globalDefaultDurationSeconds = defDur;
      const maxDur = Number(settings.globalMaxDurationSeconds);
      if (!Number.isNaN(maxDur) && maxDur > 0) payload.globalMaxDurationSeconds = maxDur;
      const ttl = Number(settings.defaultTtl);
      if (!Number.isNaN(ttl) && ttl > 0) payload.defaultTtl = ttl;
      await upsertTenantPagingSettingsAdmin(tenantId, payload);
      await loadSettings();
    } catch (e) {
      setSettingsError(String(e?.message || 'Failed to save paging settings'));
    } finally {
      setSettingsSaving(false);
    }
  };

  const loadRolePolicies = async () => {
    if (!tenantId) return;
    setRolesLoading(true);
    setRolesError('');
    try {
      const roles = await getTenantPagingRolePoliciesAdmin(tenantId);
      setRolePolicies(roles || {});
    } catch (e) {
      setRolesError(String(e?.message || 'Failed to load role policies'));
    } finally {
      setRolesLoading(false);
    }
  };

  const saveRolePolicies = async () => {
    if (!tenantId) return;
    setRolesSaving(true);
    setRolesError('');
    try {
      await setTenantPagingRolePoliciesAdmin(tenantId, rolePolicies || {});
      await loadRolePolicies();
    } catch (e) {
      setRolesError(String(e?.message || 'Failed to save role policies'));
    } finally {
      setRolesSaving(false);
    }
  };

  const loadZones = async () => {
    if (!tenantId) return;
    setZonesLoading(true);
    setZonesError('');
    try {
      const list = await getTenantPagingZonesAdmin(tenantId);
      const zonesList = Array.isArray(list) ? list : [];
      setZones(zonesList);

      // Hydrate friendly labels for users in this tenant so the table can show names
      try {
        const resp = await fetchUsersFromAuth({ tenantId });
        let users = [];
        if (Array.isArray(resp)) users = resp;
        else if (resp?.data && Array.isArray(resp.data)) users = resp.data;
        else if (resp?.data && Array.isArray(resp.data.users)) users = resp.data.users;
        else if (Array.isArray(resp?.users)) users = resp.users;

        const map = {};
        users.forEach((u) => {
          const uid = u.id || u.uid;
          if (!uid) return;
          const label =
            u.displayName ||
            [u.firstName, u.lastName].filter(Boolean).join(' ') ||
            u.email ||
            uid;
          const email = u.email || '';
          map[uid] = { label, email };
        });
        setUserLabelMap(map);
      } catch {
        // If user lookup fails, fall back to showing raw IDs in the table
      }
    } catch (e) {
      setZonesError(String(e?.message || 'Failed to load paging zones'));
    } finally {
      setZonesLoading(false);
    }
  };

  const handleAddZone = () => {
    setZoneDialogInitial(null);
    setZoneDevices([]);
    setZoneDialogOpen(true);
  };

  const handleEditZone = async (zone) => {
    setZoneDialogInitial(zone);
    setZoneDialogOpen(true);
    setZoneDevices([]);
    if (!tenantId || !zone?.id) return;
    setZoneDevicesLoading(true);
    try {
      const devices = await getTenantPagingZoneDevicesAdmin(tenantId, zone.id);
      setZoneDevices(Array.isArray(devices) ? devices : []);
    } catch (e) {
      // Surface error in zonesError to avoid extra state
      setZonesError(String(e?.message || 'Failed to load devices for zone'));
    } finally {
      setZoneDevicesLoading(false);
    }
  };

  const handleDeleteZone = async (zone) => {
    if (!tenantId || !zone?.id) return;
    const ok = window.confirm(`Delete paging zone "${zone.name || zone.id}"?`);
    if (!ok) return;
    setZonesLoading(true);
    setZonesError('');
    try {
      await deleteTenantPagingZoneAdmin(tenantId, zone.id);
      await loadZones();
    } catch (e) {
      setZonesError(String(e?.message || 'Failed to delete paging zone'));
    } finally {
      setZonesLoading(false);
    }
  };

  const handleZoneSubmit = async (payload, deviceIds) => {
    if (!tenantId) return;
    setZoneDialogSaving(true);
    setZonesError('');
    try {
      let zone = null;
      if (zoneDialogInitial && zoneDialogInitial.id) {
        zone = await updateTenantPagingZoneAdmin(tenantId, zoneDialogInitial.id, payload);
      } else {
        zone = await createTenantPagingZoneAdmin(tenantId, payload);
      }
      const zoneId = zone?.id || zoneDialogInitial?.id;
      if (zoneId && Array.isArray(deviceIds)) {
        await setTenantPagingZoneDevicesAdmin(tenantId, zoneId, deviceIds);
      }
      await loadZones();
      setZoneDialogOpen(false);
    } catch (e) {
      setZonesError(String(e?.message || 'Failed to save paging zone'));
    } finally {
      setZoneDialogSaving(false);
    }
  };

  const handleAddRole = () => {
    setRolePolicies((prev) => {
      const next = { ...(prev || {}) };
      let base = 'new-role';
      let key = base;
      let i = 1;
      while (next[key]) {
        key = `${base}-${i++}`;
      }
      next[key] = { canPage: true };
      return next;
    });
  };

  const handleDeleteRole = (roleKey) => {
    setRolePolicies((prev) => {
      const next = { ...(prev || {}) };
      delete next[roleKey];
      return next;
    });
  };

  const handleRoleNameChange = (oldKey, newKey) => {
    setRolePolicies((prev) => {
      const src = prev || {};
      const value = src[oldKey] || {};
      const next = { ...src };
      delete next[oldKey];
      const trimmed = newKey.trim();
      if (trimmed) {
        next[trimmed] = value;
      }
      return next;
    });
  };

  const handleRoleFieldChange = (roleKey, field, value) => {
    setRolePolicies((prev) => {
      const src = prev || {};
      const current = src[roleKey] || {};
      return {
        ...src,
        [roleKey]: {
          ...current,
          [field]: value,
        },
      };
    });
  };

  const roleEntries = Object.entries(rolePolicies || {});

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Paper elevation={0} sx={{ p: 2, border: '1px solid #e5e7eb' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6" component="h3">
            Tenant Paging Settings
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              startIcon={<Refresh />}
              onClick={loadSettings}
              disabled={settingsLoading}
            >
              Refresh
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={saveSettings}
              disabled={settingsSaving || settingsLoading}
            >
              {settingsSaving ? 'Saving…' : 'Save Settings'}
            </Button>
          </Stack>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Each zone targets multicast groups and assigned devices. You can explicitly allowlist
          users per zone from here.
        </Typography>
        <Stack direction="row" spacing={3} alignItems="center" sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={!!settings.pagingEnabled}
                onChange={(e) => setSettings((prev) => ({ ...prev, pagingEnabled: e.target.checked }))}
              />
            }
            label="Paging Enabled"
          />
          {settingsLoading && <CircularProgress size={20} />}
        </Stack>
        <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
          <TextField
            label="Global Default Duration (seconds)"
            type="number"
            sx={{ width: 260 }}
            value={settings.globalDefaultDurationSeconds}
            onChange={(e) =>
              setSettings((prev) => ({ ...prev, globalDefaultDurationSeconds: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Global Max Duration (seconds)"
            type="number"
            sx={{ width: 260 }}
            value={settings.globalMaxDurationSeconds}
            onChange={(e) =>
              setSettings((prev) => ({ ...prev, globalMaxDurationSeconds: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Default TTL"
            type="number"
            sx={{ width: 200 }}
            value={settings.defaultTtl}
            onChange={(e) => setSettings((prev) => ({ ...prev, defaultTtl: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Enable paging to allow users to start multicast RTP pages. Per-zone access is controlled
          by zone allowlists and device assignments below.
        </Typography>
        {settingsError && (
          <Typography variant="body2" color="error">
            {settingsError}
          </Typography>
        )}
      </Paper>

      <Paper elevation={0} sx={{ p: 2, border: '1px solid #e5e7eb' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6" component="h3">
            Paging Zones
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              startIcon={<Refresh />}
              onClick={loadZones}
              disabled={zonesLoading}
            >
              Refresh
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddZone}
              disabled={!tenantId}
            >
              Add Zone
            </Button>
          </Stack>
        </Stack>
        {zonesError && (
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            {zonesError}
          </Typography>
        )}
        {zonesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={26} />
          </Box>
        ) : zones.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No paging zones configured yet.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Durations</TableCell>
                <TableCell>Users</TableCell>
                <TableCell>Multicast Groups</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {zones.map((z) => (
                <TableRow key={z.id} hover>
                  <TableCell>{z.name || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={z.active === false ? 'inactive' : 'active'}
                      color={z.active === false ? 'default' : 'success'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {z.defaultDurationSeconds ? `Default ${z.defaultDurationSeconds}s` : 'Default —'}
                    </Typography>
                    <Typography variant="body2">
                      {z.maxDurationSeconds ? `Max ${z.maxDurationSeconds}s` : 'Max —'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {Array.isArray(z.allowedUserIds) && z.allowedUserIds.length > 0 ? (
                      z.allowedUserIds.map((uid) => {
                        const info = userLabelMap[uid];
                        const label = info?.label || uid;
                        return (
                          <Chip
                            key={uid}
                            label={label}
                            size="small"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        );
                      })
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {Array.isArray(z.multicastGroups) && z.multicastGroups.length > 0 ? (
                      z.multicastGroups.map((m, idx) => (
                        <Tooltip
                          key={`${z.id}_mg_${idx}`}
                          title={`TTL: ${m.ttl ?? '—'}, Gain: ${m.gainDb ?? '—'}`}
                        >
                          <Chip
                            label={`${m.group || '?'}:${m.port || '?'}`}
                            size="small"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        </Tooltip>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                      <TroubleshootThis pins={{ zoneId: z.id, kind: 'rtp', label: z.name || z.label || z.id }} />
                      <IconButton size="small" onClick={() => handleEditZone(z)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteZone(z)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {SHOW_PAGING_ROLE_POLICIES && (
        <Paper elevation={0} sx={{ p: 2, border: '1px solid #e5e7eb' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6" component="h3">
              Role-Based Paging Policies
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                startIcon={<Refresh />}
                onClick={loadRolePolicies}
                disabled={rolesLoading}
              >
                Refresh
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Add />}
                onClick={handleAddRole}
              >
                Add Role
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={saveRolePolicies}
                disabled={rolesSaving || rolesLoading}
              >
                {rolesSaving ? 'Saving…' : 'Save Policies'}
              </Button>
            </Stack>
          </Stack>
          {rolesError && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              {rolesError}
            </Typography>
          )}
          {rolesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={26} />
            </Box>
          ) : roleEntries.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No paging role policies configured yet.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Role</TableCell>
                  <TableCell>Can Page</TableCell>
                  <TableCell>Default Duration (seconds)</TableCell>
                  <TableCell>Max Duration (seconds)</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roleEntries.map(([roleKey, policy], idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>
                      <TextField
                        size="small"
                        value={roleKey}
                        onChange={(e) => handleRoleNameChange(roleKey, e.target.value)}
                        placeholder="admin, tenant_admin, superintendent, principal, teacher"
                      />
                    </TableCell>
                    <TableCell>
                      <Checkbox
                        checked={!!policy?.canPage}
                        onChange={(e) => handleRoleFieldChange(roleKey, 'canPage', e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={policy?.defaultDurationSeconds ?? ''}
                        onChange={(e) =>
                          handleRoleFieldChange(
                            roleKey,
                            'defaultDurationSeconds',
                            e.target.value === '' ? undefined : Number(e.target.value)
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={policy?.maxDurationSeconds ?? ''}
                        onChange={(e) =>
                          handleRoleFieldChange(
                            roleKey,
                            'maxDurationSeconds',
                            e.target.value === '' ? undefined : Number(e.target.value)
                          )
                        }
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleDeleteRole(roleKey)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      <ZoneDialog
        open={zoneDialogOpen}
        onClose={() => setZoneDialogOpen(false)}
        onSubmit={handleZoneSubmit}
        initial={zoneDialogInitial}
        devices={zoneDialogInitial?.id ? zoneDevices : null}
        loadingDevices={zoneDevicesLoading || zoneDialogSaving}
        tenantId={tenantId}
        userLabelMap={userLabelMap}
      />
    </Box>
  );
}
